/**
 * A container's schedule from the Danish clock onto the country's run day —
 * the ONE function the planner (toDayPlanInput) and the editor's timing
 * mirror both call, with the same run date, so they agree by construction.
 * Start and pinned drops map into the run day; an end with a Specified
 * start is anchored to the start's Danish date (+1 day when ≥ 1440 — never
 * a Danish duration, wrong across a change night); an end with a floating
 * start lands after the window opens. Ticks stay local durations.
 * For Media Devoted | Created By Ciro Autiero
 */
import { addDaysIso, localDateInTz, wallTimeToUtc } from "../wall-clock";
import { type CountryDay, DANISH_TZ, dkToLocal, localInstant, localToDk, mod1440, runDayMinute } from "./danish-clock";

/** The latest country minute a schedule may reach (the next day's 23:59). */
const LAST_MIN = 2879;

export interface CountryClockDay extends CountryDay {
  /** The country's window opens (country-local) — where a floating start begins. */
  windowStartMin: number;
}

export interface DanishSchedule {
  startMode: string;
  startAtMin: number | null;
  endAtMin: number | null;
  slots: Array<{ pinnedAtMin?: number | null }>;
}

/** The same container with start, end and pinned drops on the country's clock for `day`. */
export function onCountryClock<T extends DanishSchedule>(c: T, day: CountryClockDay): T {
  if (!day.tz) return c;
  const fixedStart = c.startMode === "FIXED_TIME" ? c.startAtMin : null;
  return {
    ...c,
    startAtMin: c.startAtMin === null ? null : dkToLocal(c.startAtMin, day),
    endAtMin: c.endAtMin === null ? null : endToLocal(c.endAtMin, fixedStart, day),
    slots: c.slots.map((s) => (s.pinnedAtMin === null || s.pinnedAtMin === undefined ? s : { ...s, pinnedAtMin: dkToLocal(s.pinnedAtMin, day) })),
  };
}

/** A Danish end on the country's clock: after a Specified start (its Danish date), else after the window opens. */
export function endToLocal(endDk: number, startDk: number | null, day: CountryClockDay): number {
  if (!day.tz) return endDk;
  const ctx = { tz: day.tz, runDate: day.runDate };
  if (startDk === null) {
    let e = dkToLocal(mod1440(endDk), day);
    while (e <= day.windowStartMin) e += 1440;
    return Math.min(LAST_MIN, e);
  }
  const startLocal = dkToLocal(startDk, day);
  const startDkDate = localDateInTz(localInstant(startLocal, ctx), DANISH_TZ);
  const endAt = wallTimeToUtc({ dateIso: addDaysIso(startDkDate, endDk >= 1440 ? 1 : 0), minutesFromMidnight: mod1440(endDk), tz: DANISH_TZ });
  const local = runDayMinute(endAt, day.runDate, day.tz);
  return Math.min(LAST_MIN, local > startLocal ? local : local + 1440);
}

/** A country end minute as the Danish end to store (+1440 when its Danish date is after the start's). */
export function localEndToDk(endLocal: number, startLocal: number | null, day: CountryDay): number {
  if (!day.tz) return endLocal;
  const end = localToDk(endLocal, day);
  if (startLocal === null) return end.dkMin;
  const ctx = { tz: day.tz, runDate: day.runDate };
  const startDate = localDateInTz(localInstant(startLocal, ctx), DANISH_TZ);
  const endDate = localDateInTz(localInstant(endLocal, ctx), DANISH_TZ);
  return endDate > startDate ? end.dkMin + 1440 : end.dkMin;
}
